<template>
  <div
    role="button"
    aria-label="Go to conversation"
    tabindex="0"
    class="rounded-lg p-3 flex gap-3 mb-1 cursor-pointer relative"
    :class="$dm('bg-white', 'dark:bg-slate-950 dark:text-white')"
    @click="goToConversation"
    @keyup.enter="goToConversation"
  >
    <div class="flex flex-col">
      <thumbnail size="36px" :username="agentName" :src="agentAvatar" />
    </div>
    <div class="custom-width">
      <div class="flex items-center justify-between pb-1">
        <span class="font-bold text-sm">{{ agentName }}</span>
        <span
          v-if="!archived"
          class="py-1 px-3 rounded-2xl text-xs border border-solid text-woot-900 flex relative"
          :class="badgeClasses"
        >
          <fluent-icon
            v-if="status === 'resolved'"
            icon="check-mark-circle"
            size="26"
            class="resolved-icon"
          />
          {{ badgeText }}
        </span>
        <span
          v-else
          class="py-1 px-3 rounded-2xl text-xs border border-solid text-woot-900 flex relative pending-badge"
        >
          {{ $t('STATUS_BADGES.ARCHIVED') }}
        </span>
      </div>

      <span
        v-if="unreadCount > 0"
        class="bg-red-50 text-red-900 font-semibold rounded-full ml-1 text-xs inline-flex gap-1 items-center transform scale-90 absolute unread-messages-badge"
        :class="{
          'mr-6': status === 'resolved',
          'mr-4': status === 'pending',
        }"
      >
        <fluent-icon icon="unread-message" size="15" />
        {{ unreadCount }}
      </span>

      <div
        v-if="isTyping"
        class="italic text-black-700 absolute bg-white typing-badge"
        :class="$dm('', 'dark:bg-slate-950 dark:text-white')"
      >
        {{ $t('AGENT_IS_TYPING', { agent: agentName }) }}
      </div>
      <div
        v-dompurify-html="formatMessage(lastMessageContent)"
        class="text-sm leading-5 text-gray-800 clamp-2"
      />
    </div>
  </div>
</template>

<script>
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import routerMixin from 'widget/mixins/routerMixin';
import FluentIcon from 'shared/components/FluentIcon/Index';
import darkMixin from 'widget/mixins/darkModeMixin';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';

export default {
  name: 'ConversationItem',
  components: { Thumbnail, FluentIcon },
  mixins: [routerMixin, darkMixin, messageFormatterMixin],
  props: {
    conversationId: {
      type: Number,
      default: 0,
    },
    conversationDid: {
      type: Number,
      default: 0,
    },
    conversationStatus: {
      type: String,
      default: '',
    },
    isTyping: {
      type: Boolean,
      default: false,
    },
    agentName: {
      type: String,
      default: '',
    },
    agentAvatar: {
      type: String,
      default: '',
    },
    lastMessage: {
      type: Object,
      default: null,
    },
    unreadCount: {
      type: Number,
      default: 0,
    },
    status: {
      type: String, // open, resolved, pending, snoozed
      default: '',
    },
    customAttributes: {
      type: Object,
      default: () => null,
    },
    inQueue: {
      type: Boolean,
      default: false,
    },
    archived: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    badgeClasses() {
      const openOrSnoozed = this.status === 'open' || this.status === 'snoozed';
      return {
        'open-and-snoozed-badge': openOrSnoozed,
        'resolved-badge': this.status === 'resolved',
        'pending-badge': this.status === 'pending',
      };
    },
    badgeText() {
      return {
        open: this.$t('STATUS_BADGES.OPEN'),
        resolved: this.$t('STATUS_BADGES.RESOLVED'),
        pending: this.$t('STATUS_BADGES.PENDING'),
        snoozed: this.$t('STATUS_BADGES.SNOOZED'),
      }[this.status];
    },
    lastMessageContent() {
      if (!this.lastMessage) return '';
      if (
        this.lastMessage.content_type === 'form' &&
        this.lastMessage.content === 'bot_form'
      ) {
        return 'Question Form';
      }
      return this.lastMessage.content;
    },
  },
  methods: {
    goToConversation() {
      window.logger.logUserAction(
        'info',
        'Conversation item clicked going to conversation',
        {
          data: {
            conversationId: this.conversationId,
            conversationDid: this.conversationDid,
            conversationStatus: this.conversationStatus,
            customAttributes: this.customAttributes,
            in_queue: this.inQueue,
            archived: this.archived,
          },
        }
      );

      const hasToLogin =
        window.hooryWebChannel.enforceIdentityUrl &&
        !this.currentUser.identifier;

      if (hasToLogin) {
        return;
      }

      // eslint-disable-next-line consistent-return
      return this.replaceRoute(
        'messages',
        {
          conversation: this.conversationId,
          conversationDid: this.conversationDid,
          conversationStatus: this.conversationStatus,
          customAttributes: this.customAttributes,
          in_queue: this.inQueue,
          archived: this.archived,
        },
        true
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.resolved-badge {
  background-color: #d7f1e8;
  border: 1px solid #23bc85;
  padding-left: 24px;
  padding-right: 6px;
}
.pending-badge {
  border: 1px solid #c4bfd7;
  background: #f6f6f7;
}
.open-and-snoozed-badge {
  border: 1px solid rgb(98 59 234 / 69%);
  background: #dbd5f2;
  color: #2c273a;
}
::v-deep .resolved-icon {
  position: absolute;
  left: 2px;
  top: 1px;
  transform: scale(0.92);
  path:first-child {
    fill: #23bc85;
  }

  path:last-child {
    fill: #d7f1e8;
  }
}
.text-new {
  font-size: 11px;
}

.unread-messages-badge {
  top: 13px;
  right: 73px;
  padding: 2px 8px;
}
.typing-badge {
  height: calc(100% - 50px);
  width: calc(100% - 61px);
  top: 42px;
  left: 59px;
  font-size: 14px;
}
.custom-width {
  width: calc(100% - 46px);
}
</style>
